import type { MediaItem, DispatchPayload, PayloadField } from './types';

// Resolve a token like {{item.title}} against a MediaItem
function resolveToken(token: string, item: MediaItem, webhookName: string): string {
  const map: Record<string, string> = {
    '{{item.id}}': item.id,
    '{{item.title}}': item.title,
    '{{item.year}}': String(item.year),
    '{{item.status}}': item.status,
    '{{item.type}}': item.mediaType,
    '{{item.rating}}': String(item.rating),
    '{{item.qualityProfile}}': item.qualityProfile ?? '',
    '{{item.network}}': item.network ?? '',
    '{{item.studio}}': item.studio ?? '',
    '{{item.seasonCount}}': String(item.seasonCount ?? ''),
    '{{item.episodeCount}}': String(item.episodeCount ?? ''),
    '{{item.runtime}}': String(item.runtime ?? ''),
    '{{item.certification}}': item.certification ?? '',
    '{{webhook}}': webhookName,
    '{{timestamp}}': new Date().toISOString(),
    '{{source}}': item.sourceInstanceName ?? 'MediaDispatch',
  };
  return map[token] ?? token;
}

function resolveValue(value: string, item: MediaItem, webhookName: string): string {
  return value.replace(/\{\{[^}]+\}\}/g, (match) => resolveToken(match, item, webhookName));
}

export function buildPayload(
  item: MediaItem,
  webhookName: string,
  extraFields?: PayloadField[]
): DispatchPayload {
  const baseItem: DispatchPayload['item'] = {
    id: item.id,
    title: item.title,
    type: item.mediaType,
    year: item.year,
    status: item.status,
    genres: item.genres,
    rating: item.rating,
    qualityProfile: item.qualityProfile ?? 'HD-1080p',
    monitored: item.monitored ?? true,
    network: item.network,
    studio: item.studio,
    ...(item.mediaType === 'series'
      ? { seasonCount: item.seasonCount, episodeCount: item.episodeCount }
      : item.mediaType === 'movie'
      ? { runtime: item.runtime, certification: item.certification }
      : {}),
    // Merge custom fields from the item itself
    ...(item.customFields ?? {}),
  };

  // Apply per-connection payload fields
  if (extraFields) {
    for (const field of extraFields) {
      if (field.enabled && field.key.trim()) {
        baseItem[field.key.trim()] = resolveValue(field.value, item, webhookName);
      }
    }
  }

  return {
    event: 'media_dispatched',
    timestamp: new Date().toISOString(),
    source: item.sourceInstanceName ?? 'MediaDispatch',
    webhook: webhookName,
    item: baseItem,
  };
}

export const PAYLOAD_TOKENS = [
  { token: '{{item.id}}', description: 'Item ID' },
  { token: '{{item.title}}', description: 'Media title' },
  { token: '{{item.year}}', description: 'Release year' },
  { token: '{{item.status}}', description: 'Item status' },
  { token: '{{item.type}}', description: '"series", "movie", or "custom"' },
  { token: '{{item.rating}}', description: 'Rating score' },
  { token: '{{item.qualityProfile}}', description: 'Quality profile' },
  { token: '{{item.network}}', description: 'Network (series)' },
  { token: '{{item.studio}}', description: 'Studio (movie)' },
  { token: '{{item.seasonCount}}', description: 'Season count' },
  { token: '{{item.episodeCount}}', description: 'Episode count' },
  { token: '{{item.runtime}}', description: 'Runtime in minutes' },
  { token: '{{item.certification}}', description: 'Age certification' },
  { token: '{{webhook}}', description: 'Webhook name' },
  { token: '{{timestamp}}', description: 'ISO 8601 dispatch time' },
  { token: '{{source}}', description: 'Source instance name' },
];
